import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { serviceAreas, services } from './data';
import ServiceAreaMap from './ServiceAreaMap';
import SunRaysHero from './SunRaysHero';

function Eyebrow({ children, dark = false }) {
	return (
		<p
			className={`text-xs font-semibold uppercase tracking-[0.18em] ${
				dark ? 'text-primary-400' : 'text-primary-600'
			}`}
		>
			<span className='inline-block w-8 h-px align-middle mr-3 bg-current opacity-60' />
			{children}
		</p>
	);
}

export default function ServiceAreas() {
	// Shared hover state so the chips and the map highlight together.
	const [activeArea, setActiveArea] = useState(null);

	return (
		<div className='bg-surface text-ink-900 antialiased'>
			<section className='relative isolate overflow-hidden border-b border-ink-100'>
				<SunRaysHero seed={2} />
				<div className='mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8'>
					<Eyebrow>Service areas</Eyebrow>
					<h1 className='font-display mt-5 text-5xl font-medium tracking-tight text-ink-900 sm:text-6xl leading-[1.05] max-w-3xl'>
						Solar help where your property is.
					</h1>
					<p className='mt-6 max-w-2xl text-base leading-7 text-ink-600'>
						Every state, city, utility, and roof has its own details. These area
						pages explain what matters before you compare solar options.
					</p>
					<Link
						to='/contact'
						className='mt-8 inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-primary-500/20 hover:bg-primary-600 transition-all'
					>
						Get a free quote
						<ArrowRightIcon className='h-4 w-4' />
					</Link>
				</div>
			</section>

			<section className='bg-white py-20 sm:py-28'>
				<div className='mx-auto max-w-7xl px-6 lg:px-8'>
					<div className='grid grid-cols-1 items-center gap-12 lg:grid-cols-2'>
						<div className='order-2 lg:order-1'>
							<Eyebrow>Where we work</Eyebrow>
							<h2 className='font-display mt-4 text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl'>
								Pick your state.
							</h2>
							<p className='mt-5 max-w-md text-base leading-7 text-ink-500'>
								Hover the map to highlight a service area, then click through
								for local incentives, utilities, and what to know before you
								compare solar options.
							</p>
							<div className='mt-8 flex flex-wrap gap-2'>
								{serviceAreas.map((area) => {
									const isActive = activeArea === area.id;
									return (
										<Link
											key={area.id}
											to={`/service-areas/${area.id}`}
											onMouseEnter={() => setActiveArea(area.id)}
											onMouseLeave={() => setActiveArea(null)}
											onFocus={() => setActiveArea(area.id)}
											onBlur={() => setActiveArea(null)}
											className={`rounded-lg px-4 py-2 text-sm font-medium ring-1 transition-all ${
												isActive
													? 'bg-primary-500 text-white ring-primary-500'
													: 'bg-surface text-ink-700 ring-ink-100 hover:ring-primary-300 hover:text-primary-700'
											}`}
										>
											{area.name}
										</Link>
									);
								})}
							</div>
						</div>
						<div className='order-1 lg:order-2'>
							<ServiceAreaMap
								active={activeArea}
								onActiveChange={setActiveArea}
							/>
						</div>
					</div>
				</div>
			</section>

			<section className='bg-surface py-20 sm:py-28 border-y border-ink-100'>
				<div className='mx-auto max-w-7xl px-6 lg:px-8'>
					<Eyebrow>All service areas</Eyebrow>
					<h2 className='font-display mt-4 mb-10 text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl'>
						Explore each area.
					</h2>
					<div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
						{serviceAreas.map((area) => (
							<Link
								key={area.id}
								to={`/service-areas/${area.id}`}
								className='group sg-card sg-card-interactive p-8'
							>
								<div className='flex items-center gap-4'>
									<div className='sg-badge h-12 w-12'>
										<MapPinIcon className='h-6 w-6' />
									</div>
									<h2 className='font-display text-2xl font-medium text-ink-900'>
										{area.label}
									</h2>
								</div>
								<p className='mt-5 text-sm leading-6 text-ink-500'>
									{area.summary}
								</p>
								<div className='mt-6 flex flex-wrap gap-2'>
									{area.markets.slice(0, 4).map((market) => (
										<span
											key={market}
											className='rounded-full bg-surface px-3 py-1 text-xs font-medium text-ink-600 ring-1 ring-ink-100'
										>
											{market}
										</span>
									))}
								</div>
								<span className='mt-8 inline-flex items-center gap-1 text-sm font-semibold text-primary-600 group-hover:gap-2 transition-all'>
									View local info
									<ArrowRightIcon className='h-4 w-4' />
								</span>
							</Link>
						))}
					</div>
				</div>
			</section>

			<section className='bg-surface py-20 border-y border-ink-100'>
				<div className='mx-auto max-w-7xl px-6 lg:px-8'>
					<div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
						<div className='max-w-2xl'>
							<Eyebrow>Services</Eyebrow>
							<h2 className='font-display mt-4 text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl'>
								Each service has its own page too.
							</h2>
						</div>
						<Link
							to='/services'
							className='inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700'
						>
							See service overview
							<ArrowRightIcon className='h-4 w-4' />
						</Link>
					</div>
					<div className='mt-10 grid grid-cols-1 gap-4 md:grid-cols-4'>
						{services.map((service) => (
							<Link
								key={service.id}
								to={`/services/${service.id}`}
								className='group sg-card sg-card-interactive p-6'
							>
								<h3 className='font-display text-xl font-medium text-ink-900'>
									{service.title}
								</h3>
							</Link>
						))}
					</div>
				</div>
			</section>
		</div>
	);
}
